import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { NextImage } from '@/components/NextImage';
import { Overlay } from '@/components/Overlay';
import { Typography } from '@/components/Typography';

import { ReactComponent as RulerSVG } from '@/assets/ruler.svg';

export const InfoBadgeWrapper = styled(Flex)`
  align-items: center;
  gap: 5px;
`;

export const StrapImageBox = styled(Box)`
  max-width: 33.33%;
`;
export const BucklesBox = styled(Flex)`
  flex-direction: column;
  width: 100%;
`;

export const TotalWrapper = styled(Flex)`
  align-items: center;
  justify-content: space-between;

  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.primaryAdd6};
    padding: ${theme.spacing(2, 0)};
    ${theme.media.sm.down} {
      justify-content: space-between;
      margin-bottom: ${theme.spacing(2)};
    }
  `}
`;
export const AddToBagButton = styled(Box)`
  ${({ theme }) => css`
    ${theme.media.sm.down} {
      width: 100%;
    }
  `}
`;
const generateGridTemplateColumns = (itemsLength: number) => {
  const columns = Array(itemsLength).fill('1fr').join(' ');
  return `${columns} auto`;
};
export const SizesContainer = styled(Box)<{ $selectItemsLength?: number }>`
  ${({ $selectItemsLength, theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    margin: ${({ theme }) => theme.spacing(1.75, 0)};
    justify-content: center;
    ${theme.media.md.up} {
      ${$selectItemsLength === 3 &&
      css`
        display: grid;
        grid-template-columns: ${generateGridTemplateColumns(
          $selectItemsLength,
        )};
      `}
    }
  `}
`;

export const SizesTitle = styled(Flex)<{
  isMobile: boolean;
  isSelected: boolean;
}>`
  text-transform: uppercase;
  justify-content: center;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing(2)};
  background: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.primaryBlue : theme.colors.white};
  cursor: pointer;
  transition-property: background-color, color;
  transition-duration: 0.3s;
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryBlue};
    color: ${({ theme }) => theme.colors.white};
  }

  ${({ isSelected, theme }) => css`
    color: ${theme.colors.secondaryGrey};
    font-weight: ${theme.fontWeights.bold};
    padding: ${theme.spacing(1, 2)};

    ${isSelected &&
    css`
      background-color: ${theme.colors.primaryBlue};
      color: ${theme.colors.white};
    `}
  `}
`;

export const OtherSizesBox = styled(Box)`
  ${({ theme }) => css`
    padding-inline: ${theme.spacing(2)};
  `}
`;

export const DrawerOverlay = styled(Overlay)`
  background: red !important;
  padding: 0 !important;
  margin-bottom: 0 !important;
  background-color: rgba(0, 0, 0, 0.6) !important;
`;

export const Wrapper = styled.aside`
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  right: 0;

  ${({
    theme: {
      colors: { shadow2, white },
      components: { addToCartDialog },
      media,
    },
  }) => css`
    box-shadow: 0px 3px 6px ${shadow2};
    background-color: ${white};

    ${media.md.up} {
      width: ${addToCartDialog.width};
    }
  `}
`;

export const HeaderWrapper = styled(Flex)`
  position: relative;
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => css`
    padding: ${theme.spacing(2, 2, 2, 2)};
    border-bottom: 1px solid ${theme.colors.primaryAdd6};
  `}
`;

export const Header = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: center;
`;

export const HeaderText = styled(Typography)`
  padding-top: 4px;
  text-transform: uppercase;

  ${({ theme }) => css`
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.h4};
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.secondary};
  `}
`;

export const CloseButton = styled.button`
  position: absolute !important;
  right: 1rem !important;
  all: unset;
  cursor: pointer;
  transform: translateX(5px);
`;

export const Body = styled(Box)`
  overflow: auto;
  min-height: calc(100dvh - 186px);
  max-height: calc(100dvh - 186px);
  ${({ theme }) => css`
    padding: ${theme.spacing(3, 3, 0, 3)};
  `}
`;

export const Footer = styled(Box)`
  justify-content: space-between;

  ${({ theme }) => css`
    background-color: ${theme.colors.white};
    padding: ${theme.spacing(0.25, 3, 3, 3)};
    gap: ${theme.spacing(2)};

    ${theme.media.sm.down} {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
    }
  `}

  > a {
    width: 100%;
  }
`;
export const StrapWrapper = styled(Flex)`
  align-items: center;

  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(1)};
    transition: ${theme.transitions.duration.standard} ease;

    padding: ${theme.spacing(1)};
    outline: 1px solid ${theme.colors.quinaryGrey};
    outline-offset: -1px;
    outline: 3px solid ${theme.colors.primaryBlue};
    outline-offset: -3px;
  `}

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const ImageWrapper = styled(Flex)`
  position: relative;
  min-width: 88px;
  max-width: 88px;
  height: 88px;
  ${({ theme }) => css`
    margin-right: ${theme.spacing(2)};
  `};
`;

export const ProductImage = styled(NextImage)`
  object-fit: contain;
  inset: 0;
  width: 100%;
`;

export const Ruler = styled(RulerSVG)`
  width: 22px;
  height: 22px;
  cursor: pointer;
  margin: ${({ theme }) => theme.spacing(1, 2)};
  margin-left: ${({ theme }) => theme.spacing(0)};
`;

export const SizeInfoText = styled(Typography)`
  text-align: center;

  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
    color: ${theme.colors.secondaryGrey};
    margin-bottom: ${theme.spacing(3)};
  `}
`;

export const BottomTextWrapper = styled(Box)`
  ${({ theme }) => css`
    background-color: #fff8e0;
    margin: ${theme.spacing(2, 0)};
    padding: ${theme.spacing(1, 1, 1, 4)};
    ul {
      list-style: disc;
    }
  `}
`;

export const AvailableSizeTitle = styled(Box)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.h4};
    font-family: ${theme.fontFamilies.italianPlate};
    color: ${theme.colors.secondary};
    font-weight: ${theme.fontWeights.semibold};
    margin-top: ${theme.spacing(4)};
    text-align: center;
    text-transform: uppercase;
  `}
`;

export const ErrorMessage = styled.div`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.h6};
    font-family: ${theme.fontFamilies.italianPlate};
    color: ${theme.colors.error};
    margin-bottom: ${theme.spacing(2)};
    text-align: center;
  `}
`;
